import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Pencil, Trash2, Search, Package, FileText, Link2, List, Image, Upload, Download, FileSpreadsheet, Loader2, Tags, Database, GripVertical, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentYear, getMonthName } from '../lib/utils';

const EXPORT_MODULES = [
  { key: 'forecast_ca', label: 'Forecast CA', icon: '📊', needsYear: true },
  { key: 'ca_reel', label: 'CA Réel', icon: '💰', needsYear: true },
  { key: 'plateformes', label: 'Revenus Plateformes', icon: '🛵', needsYear: true },
  { key: 'depenses', label: 'Dépenses', icon: '📑', needsYear: true },
  { key: 'recap_depenses', label: 'Récap Dépenses', icon: '📋', needsYear: true, yearRequired: true },
  { key: 'tva', label: 'TVA', icon: '🏛️', needsYear: true, yearRequired: true },
  { key: 'planning', label: 'Planning', icon: '📅', needsYear: false },
  { key: 'verification_especes', label: 'Vérification Espèces', icon: '💶', needsYear: false },
  { key: 'inventaire', label: 'Inventaire', icon: '📦', needsYear: false },
  { key: 'traiteur', label: 'Commandes Traiteur', icon: '👨‍🍳', needsYear: false },
];

function ExportsPanel({ api }) {
  const [exportYear, setExportYear] = useState(getCurrentYear().toString());
  const [exportMonth, setExportMonth] = useState('all');
  const [downloading, setDownloading] = useState({});

  const handleExport = async (moduleKey, format) => {
    const dlKey = `${moduleKey}_${format}`;
    setDownloading(prev => ({ ...prev, [dlKey]: true }));
    try {
      const params = new URLSearchParams({ format });
      if (exportYear && exportYear !== 'all') params.append('annee', exportYear);
      if (exportMonth && exportMonth !== 'all') params.append('mois', exportMonth);
      const res = await api.get(`/export/${moduleKey}?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute('download', `${moduleKey}_${exportYear || 'all'}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erreur export ${format.toUpperCase()}`);
    } finally {
      setDownloading(prev => ({ ...prev, [dlKey]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" /> Centre d'exports
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Exportez vos données en Excel (.xlsx) ou PDF. Sélectionnez l'année et le mois pour filtrer.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Année :</Label>
              <Select value={exportYear} onValueChange={setExportYear}>
                <SelectTrigger className="w-[120px]" data-testid="export-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Mois :</Label>
              <Select value={exportMonth} onValueChange={setExportMonth}>
                <SelectTrigger className="w-[160px]" data-testid="export-month-select">
                  <SelectValue placeholder="Tous les mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les mois</SelectItem>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {EXPORT_MODULES.map(mod => {
          const xlsKey = `${mod.key}_xlsx`;
          const pdfKey = `${mod.key}_pdf`;
          return (
            <Card key={mod.key} className="hover:border-primary/40 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-base">{mod.label}</h3>
                    {mod.needsYear && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Filtré par année{exportMonth !== 'all' ? ' et mois' : ''}
                      </p>
                    )}
                    {!mod.needsYear && (
                      <p className="text-xs text-muted-foreground mt-1">Toutes les données</p>
                    )}
                  </div>
                  <span className="text-2xl">{mod.icon}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => handleExport(mod.key, 'xlsx')}
                    disabled={downloading[xlsKey]}
                    data-testid={`export-${mod.key}-xlsx`}
                  >
                    {downloading[xlsKey] ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-green-600" />}
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => handleExport(mod.key, 'pdf')}
                    disabled={downloading[pdfKey]}
                    data-testid={`export-${mod.key}-pdf`}
                  >
                    {downloading[pdfKey] ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-red-600" />}
                    PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export function ParametresPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('produits');
  const [produits, setProduits] = useState([]);
  const [refDepenses, setRefDepenses] = useState([]);
  const [mappingLinxo, setMappingLinxo] = useState([]);
  const [listes, setListes] = useState({ employe: [], mode_paiement: [], plateforme: [], annee: [] });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [produitDialogOpen, setProduitDialogOpen] = useState(false);
  const [refDepenseDialogOpen, setRefDepenseDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  // Login image state
  const [loginImage, setLoginImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchLoginImage();
  }, []);

  const fetchLoginImage = async () => {
    try {
      const res = await api.get('/settings/login-image');
      if (res.data.url) {
        setLoginImage(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      }
    } catch (e) { /* no image set */ }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/settings/login-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLoginImage(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}?t=${Date.now()}`);
      toast.success('Photo de connexion mise a jour');
    } catch {
      toast.error('Erreur upload image');
    } finally {
      setUploading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, depRes, mapRes, empRes, payRes, platRes, yearRes] = await Promise.all([
        api.get('/produits'),
        api.get('/ref-depenses'),
        api.get('/mapping-linxo'),
        api.get('/listes/employe'),
        api.get('/listes/mode_paiement'),
        api.get('/listes/plateforme'),
        api.get('/listes/annee')
      ]);
      setProduits(prodRes.data);
      setRefDepenses(depRes.data);
      setMappingLinxo(mapRes.data);
      setListes({
        employe: empRes.data,
        mode_paiement: payRes.data,
        plateforme: platRes.data,
        annee: yearRes.data
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Produits CRUD
  const ProduitForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || {
      sous_categorie: '', categorie_interne: '', code: '', nom: '', tva: 0.1, prix_janv_25: 0
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) {
          await api.put(`/produits/${item.id}`, form);
          toast.success('Produit modifié');
        } else {
          await api.post('/produits', form);
          toast.success('Produit créé');
        }
        fetchData();
        onClose();
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="produit-nom" />
          </div>
          <div className="space-y-2">
            <Label>Code</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required data-testid="produit-code" />
          </div>
          <div className="space-y-2">
            <Label>Sous-catégorie</Label>
            <Input value={form.sous_categorie} onChange={(e) => setForm({ ...form, sous_categorie: e.target.value })} required data-testid="produit-sous-cat" />
          </div>
          <div className="space-y-2">
            <Label>Catégorie interne</Label>
            <Input value={form.categorie_interne} onChange={(e) => setForm({ ...form, categorie_interne: e.target.value })} required data-testid="produit-cat" />
          </div>
          <div className="space-y-2">
            <Label>TVA (%)</Label>
            <Input type="number" step="0.01" value={form.tva * 100} onChange={(e) => setForm({ ...form, tva: parseFloat(e.target.value) / 100 })} required data-testid="produit-tva" />
          </div>
          <div className="space-y-2">
            <Label>Prix (€)</Label>
            <Input type="number" step="0.01" value={form.prix_janv_25} onChange={(e) => setForm({ ...form, prix_janv_25: parseFloat(e.target.value) })} required data-testid="produit-prix" />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" data-testid="produit-submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  // Ref Depense CRUD
  const RefDepenseForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || {
      categorie: '', categorie_interne: '', categorie_interne2: '', libelle: '', tva: 0.2
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) {
          await api.put(`/ref-depenses/${item.id}`, form);
          toast.success('Référentiel modifié');
        } else {
          await api.post('/ref-depenses', form);
          toast.success('Référentiel créé');
        }
        fetchData();
        onClose();
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Libellé</Label>
            <Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Input value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Catégorie interne</Label>
            <Input value={form.categorie_interne} onChange={(e) => setForm({ ...form, categorie_interne: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Catégorie interne 2</Label>
            <Input value={form.categorie_interne2 || ''} onChange={(e) => setForm({ ...form, categorie_interne2: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>TVA (%)</Label>
            <Input type="number" step="0.01" value={form.tva * 100} onChange={(e) => setForm({ ...form, tva: parseFloat(e.target.value) / 100 })} required />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  // Mapping Linxo CRUD
  const MappingForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item || {
      categorie_linxo: '', categorie_interne: '', sous_categorie: '', type: 'Dépense', taux_tva: 0.2
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) {
          await api.put(`/mapping-linxo/${item.id}`, form);
          toast.success('Mapping modifié');
        } else {
          await api.post('/mapping-linxo', form);
          toast.success('Mapping créé');
        }
        fetchData();
        onClose();
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Catégorie Linxo</Label>
            <Input value={form.categorie_linxo} onChange={(e) => setForm({ ...form, categorie_linxo: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Catégorie interne</Label>
            <Input value={form.categorie_interne} onChange={(e) => setForm({ ...form, categorie_interne: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Sous-catégorie</Label>
            <Input value={form.sous_categorie} onChange={(e) => setForm({ ...form, sous_categorie: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <option value="Dépense">Dépense</option>
              <option value="Encaissement">Encaissement</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Taux TVA (%)</Label>
            <Input type="number" step="0.01" value={form.taux_tva * 100} onChange={(e) => setForm({ ...form, taux_tva: parseFloat(e.target.value) / 100 })} required />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  const handleDelete = async (type, id) => {
    try {
      await api.delete(`/${type}/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddListe = async (type, valeur) => {
    if (!valeur.trim()) return;
    try {
      await api.post(`/listes?type_liste=${type}&valeur=${valeur}`);
      toast.success('Ajouté');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredProduits = produits.filter(p =>
    p.nom.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase())
  );

  const filteredRefDepenses = refDepenses.filter(r =>
    r.libelle.toLowerCase().includes(search.toLowerCase()) ||
    r.categorie.toLowerCase().includes(search.toLowerCase())
  );

  const filteredMappings = mappingLinxo.filter(m =>
    m.categorie_linxo.toLowerCase().includes(search.toLowerCase()) ||
    m.categorie_interne.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="module-header">
        <h1 className="module-title">Paramètres</h1>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="params-search"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7 max-w-4xl">
          <TabsTrigger value="categories" className="gap-2" data-testid="tab-categories">
            <Tags className="w-4 h-4" /> Catégories
          </TabsTrigger>
          <TabsTrigger value="produits" className="gap-2" data-testid="tab-produits">
            <Package className="w-4 h-4" /> Produits
          </TabsTrigger>
          <TabsTrigger value="depenses" className="gap-2" data-testid="tab-depenses">
            <FileText className="w-4 h-4" /> Dépenses
          </TabsTrigger>
          <TabsTrigger value="mapping" className="gap-2" data-testid="tab-mapping">
            <Link2 className="w-4 h-4" /> Mapping
          </TabsTrigger>
          <TabsTrigger value="listes" className="gap-2" data-testid="tab-listes">
            <List className="w-4 h-4" /> Listes
          </TabsTrigger>
          <TabsTrigger value="exports" className="gap-2" data-testid="tab-exports">
            <Download className="w-4 h-4" /> Exports
          </TabsTrigger>
          <TabsTrigger value="apparence" className="gap-2" data-testid="tab-apparence">
            <Image className="w-4 h-4" /> Apparence
          </TabsTrigger>
          <TabsTrigger value="transmission" className="gap-2" data-testid="tab-transmission">
            <FileText className="w-4 h-4" /> Transmission
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <CategoriesManager api={api} />
        </TabsContent>

        {/* Produits Tab */}
        <TabsContent value="produits">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Produits ({filteredProduits.length})</CardTitle>
              <Button className="gap-2" data-testid="add-produit-btn" onClick={() => { setEditItem(null); setProduitDialogOpen(true); }}>
                <Plus className="w-4 h-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Sous-catégorie</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="text-right">TVA</TableHead>
                    <TableHead className="text-right">Prix</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProduits.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-xs">{item.code}</TableCell>
                      <TableCell className="font-medium">{item.nom}</TableCell>
                      <TableCell>{item.sous_categorie}</TableCell>
                      <TableCell>{item.categorie_interne}</TableCell>
                      <TableCell className="text-right tabular-nums">{(item.tva * 100).toFixed(0)}%</TableCell>
                      <TableCell className="text-right tabular-nums">{item.prix_janv_25.toFixed(2)} €</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setProduitDialogOpen(true); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ce produit ?</AlertDialogTitle>
                                <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete('produits', item.id)}>Supprimer</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Dialog open={produitDialogOpen} onOpenChange={setProduitDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Modifier' : 'Nouveau'} Produit</DialogTitle>
              </DialogHeader>
              <ProduitForm item={editItem} onClose={() => setProduitDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Ref Depenses Tab */}
        <TabsContent value="depenses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Référentiel Dépenses ({filteredRefDepenses.length})</CardTitle>
              <Button className="gap-2" data-testid="add-ref-depense-btn" onClick={() => { setEditItem(null); setRefDepenseDialogOpen(true); }}>
                <Plus className="w-4 h-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Cat. Interne</TableHead>
                    <TableHead className="text-right">TVA</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRefDepenses.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.libelle}</TableCell>
                      <TableCell>{item.categorie}</TableCell>
                      <TableCell>{item.categorie_interne}</TableCell>
                      <TableCell className="text-right tabular-nums">{(item.tva * 100).toFixed(0)}%</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setRefDepenseDialogOpen(true); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ?</AlertDialogTitle>
                                <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete('ref-depenses', item.id)}>Supprimer</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Dialog open={refDepenseDialogOpen} onOpenChange={setRefDepenseDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Modifier' : 'Nouveau'} Référentiel</DialogTitle>
              </DialogHeader>
              <RefDepenseForm item={editItem} onClose={() => setRefDepenseDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Mapping Linxo Tab */}
        <TabsContent value="mapping">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Mapping Linxo ({filteredMappings.length})</CardTitle>
              <Button className="gap-2" data-testid="add-mapping-btn" onClick={() => { setEditItem(null); setMappingDialogOpen(true); }}>
                <Plus className="w-4 h-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Catégorie Linxo</TableHead>
                    <TableHead>Cat. Interne</TableHead>
                    <TableHead>Sous-catégorie</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">TVA</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMappings.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.categorie_linxo}</TableCell>
                      <TableCell>{item.categorie_interne}</TableCell>
                      <TableCell>{item.sous_categorie}</TableCell>
                      <TableCell>{item.type}</TableCell>
                      <TableCell className="text-right tabular-nums">{(item.taux_tva * 100).toFixed(0)}%</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setMappingDialogOpen(true); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ?</AlertDialogTitle>
                                <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete('mapping-linxo', item.id)}>Supprimer</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Modifier' : 'Nouveau'} Mapping</DialogTitle>
              </DialogHeader>
              <MappingForm item={editItem} onClose={() => setMappingDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Listes Tab */}
        <TabsContent value="listes">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {['employe', 'mode_paiement', 'plateforme', 'annee'].map(type => (
              <Card key={type}>
                <CardHeader>
                  <CardTitle className="capitalize">
                    {type === 'employe' ? 'Employés' :
                     type === 'mode_paiement' ? 'Modes de Paiement' :
                     type === 'plateforme' ? 'Plateformes' : 'Années'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input
                      id={`new-${type}`}
                      placeholder="Nouvelle valeur..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddListe(type, e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById(`new-${type}`);
                        handleAddListe(type, input.value);
                        input.value = '';
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {listes[type].map(item => (
                      <div key={item.id} className="flex items-center gap-1 px-3 py-1 bg-secondary rounded-full text-sm">
                        {item.valeur}
                        <button
                          onClick={() => handleDelete('listes', item.id)}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Exports Tab */}
        <TabsContent value="exports">
          <ExportsPanel api={api} />
        </TabsContent>

        {/* Apparence Tab */}
        <TabsContent value="apparence">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" /> Photo de la page de connexion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Cette image est affichée sur la page de connexion de l'application.
                </p>
                {loginImage && (
                  <div className="relative rounded-xl overflow-hidden border border-border max-w-lg">
                    <img src={loginImage} alt="Login" className="w-full h-64 object-cover" data-testid="login-image-preview" />
                  </div>
                )}
                <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="login-image-input" />
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="change-login-photo-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Upload en cours...' : 'Changer la photo'}
                </Button>
              </CardContent>
            </Card>
            <SeedDataPanel api={api} />
          </div>
        </TabsContent>

        {/* Droits Tab removed - managed in Admin > Users */}

        <TabsContent value="transmission">
          <TransmissionConfigPanel api={api} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function SeedDataPanel({ api }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [details, setDetails] = useState(null);

  const handleImport = async () => {
    setLoading(true);
    try {
      const res = await api.post('/admin/seed-data');
      setDetails(res.data.details);
      setDone(true);
      toast.success('Données importées avec succès !');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-amber-200 dark:border-amber-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" /> Importer les données
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Importez vos données depuis l'environnement de preview (inventaire, dépenses, revenus, planning, etc.). 
          Cette action <strong>remplace</strong> les données existantes (sauf les utilisateurs).
        </p>
        {done && details ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Import terminé
            </div>
            <div className="max-h-48 overflow-auto rounded-lg border p-3 text-xs space-y-1">
              {Object.entries(details).map(([col, info]) => (
                <div key={col} className="flex justify-between">
                  <span className="text-muted-foreground">{col}</span>
                  <span>{info}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Button
            variant="default"
            className="gap-2"
            onClick={handleImport}
            disabled={loading}
            data-testid="btn-seed-data"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {loading ? 'Import en cours...' : 'Importer les données preview'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


function CategoriesManager({ api }) {
  const [categories, setCategories] = useState([]);
  const [sousCategories, setSousCategories] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [newSous, setNewSous] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingSous, setEditingSous] = useState(null);
  const [editingSousName, setEditingSousName] = useState('');

  const fetchCats = useCallback(async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data.categories || []);
      setSousCategories(res.data.sous_categories || []);
    } catch { toast.error('Erreur chargement'); }
  }, [api]);

  useEffect(() => { fetchCats(); }, [fetchCats]);

  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post('/categories', { nom: newCat.trim() });
      setNewCat('');
      fetchCats();
      toast.success('Catégorie ajoutée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const updateCat = async (id) => {
    if (!editingCatName.trim()) return;
    try {
      await api.put(`/categories/${id}`, { nom: editingCatName.trim() });
      setEditingCat(null);
      fetchCats();
      toast.success('Catégorie modifiée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteCat = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      fetchCats();
      toast.success('Catégorie supprimée');
    } catch { toast.error('Erreur'); }
  };

  const addSous = async () => {
    if (!newSous.trim()) return;
    try {
      await api.post('/sous-categories', { nom: newSous.trim() });
      setNewSous('');
      fetchCats();
      toast.success('Sous-catégorie ajoutée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const updateSous = async (id) => {
    if (!editingSousName.trim()) return;
    try {
      await api.put(`/sous-categories/${id}`, { nom: editingSousName.trim() });
      setEditingSous(null);
      fetchCats();
      toast.success('Sous-catégorie modifiée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteSous = async (id) => {
    try {
      await api.delete(`/sous-categories/${id}`);
      fetchCats();
      toast.success('Sous-catégorie supprimée');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* CATEGORIES */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Catégories ({categories.length})</CardTitle>
          <p className="text-xs text-muted-foreground">Utilisées dans Import Linxo, Dépenses, Récap</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Nouvelle catégorie..." className="h-8 text-sm" onKeyDown={e => e.key === 'Enter' && addCat()} data-testid="new-cat-input" />
            <Button size="sm" onClick={addCat} className="gap-1 h-8" data-testid="add-cat-btn"><Plus className="w-3.5 h-3.5" /> Ajouter</Button>
          </div>
          <div className="max-h-[400px] overflow-auto space-y-1">
            {categories.map(c => (
              <div key={c.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 group">
                {editingCat === c.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editingCatName} onChange={e => setEditingCatName(e.target.value)} className="h-7 text-sm flex-1" onKeyDown={e => e.key === 'Enter' && updateCat(c.id)} autoFocus />
                    <Button size="icon" className="h-7 w-7" onClick={() => updateCat(c.id)}><Search className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingCat(null)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm">{c.nom}</span>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditingCat(c.id); setEditingCatName(c.nom); }}><Pencil className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteCat(c.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* SOUS-CATEGORIES */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sous-catégories ({sousCategories.length})</CardTitle>
          <p className="text-xs text-muted-foreground">Précision optionnelle pour chaque dépense</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newSous} onChange={e => setNewSous(e.target.value)} placeholder="Nouvelle sous-catégorie..." className="h-8 text-sm" onKeyDown={e => e.key === 'Enter' && addSous()} data-testid="new-sous-cat-input" />
            <Button size="sm" onClick={addSous} className="gap-1 h-8" data-testid="add-sous-cat-btn"><Plus className="w-3.5 h-3.5" /> Ajouter</Button>
          </div>
          <div className="max-h-[400px] overflow-auto space-y-1">
            {sousCategories.map(c => (
              <div key={c.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 group">
                {editingSous === c.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editingSousName} onChange={e => setEditingSousName(e.target.value)} className="h-7 text-sm flex-1" onKeyDown={e => e.key === 'Enter' && updateSous(c.id)} autoFocus />
                    <Button size="icon" className="h-7 w-7" onClick={() => updateSous(c.id)}><Search className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingSous(null)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm">{c.nom}</span>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditingSous(c.id); setEditingSousName(c.nom); }}><Pencil className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteSous(c.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}




function TransmissionConfigPanel({ api }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/settings/transmission-config');
        setConfig(res.data);
      } catch { toast.error('Erreur chargement config'); }
      setLoading(false);
    };
    load();
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/transmission-config', config);
      toast.success('Configuration enregistrée');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const toggleSection = (sIdx) => {
    setConfig(prev => {
      const next = { ...prev, sections: [...prev.sections] };
      next.sections[sIdx] = { ...next.sections[sIdx], enabled: !next.sections[sIdx].enabled };
      return next;
    });
  };

  const toggleField = (sIdx, fIdx) => {
    setConfig(prev => {
      const next = { ...prev, sections: [...prev.sections] };
      next.sections[sIdx] = { ...next.sections[sIdx], fields: [...next.sections[sIdx].fields] };
      next.sections[sIdx].fields[fIdx] = { ...next.sections[sIdx].fields[fIdx], enabled: !next.sections[sIdx].fields[fIdx].enabled };
      return next;
    });
  };

  const toggleChecklist = (cIdx) => {
    setConfig(prev => {
      const next = { ...prev, checklist: [...prev.checklist] };
      next.checklist[cIdx] = { ...next.checklist[cIdx], enabled: !next.checklist[cIdx].enabled };
      return next;
    });
  };

  const updateFieldLabel = (sIdx, fIdx, label) => {
    setConfig(prev => {
      const next = { ...prev, sections: [...prev.sections] };
      next.sections[sIdx] = { ...next.sections[sIdx], fields: [...next.sections[sIdx].fields] };
      next.sections[sIdx].fields[fIdx] = { ...next.sections[sIdx].fields[fIdx], label };
      return next;
    });
  };

  const updateSectionLabel = (sIdx, label) => {
    setConfig(prev => {
      const next = { ...prev, sections: [...prev.sections] };
      next.sections[sIdx] = { ...next.sections[sIdx], label };
      return next;
    });
  };

  const updateChecklistLabel = (cIdx, label) => {
    setConfig(prev => {
      const next = { ...prev, checklist: [...prev.checklist] };
      next.checklist[cIdx] = { ...next.checklist[cIdx], label };
      return next;
    });
  };

  const addField = (sIdx) => {
    setConfig(prev => {
      const next = { ...prev, sections: [...prev.sections] };
      const section = next.sections[sIdx];
      const newId = `${section.id}_custom_${Date.now()}`;
      next.sections[sIdx] = { ...section, fields: [...section.fields, { id: newId, label: 'Nouveau champ', type: 'textarea', enabled: true }] };
      return next;
    });
  };

  const removeField = (sIdx, fIdx) => {
    setConfig(prev => {
      const next = { ...prev, sections: [...prev.sections] };
      next.sections[sIdx] = { ...next.sections[sIdx], fields: next.sections[sIdx].fields.filter((_, i) => i !== fIdx) };
      return next;
    });
  };

  const addChecklist = () => {
    setConfig(prev => {
      const newId = `checklist_custom_${Date.now()}`;
      return { ...prev, checklist: [...prev.checklist, { id: newId, label: 'Nouveau check', enabled: true }] };
    });
  };

  const removeChecklist = (cIdx) => {
    setConfig(prev => ({ ...prev, checklist: prev.checklist.filter((_, i) => i !== cIdx) }));
  };

  if (loading || !config) return <p className="text-center text-muted-foreground py-8">Chargement...</p>;

  const sectionColors = {
    client: 'bg-blue-500',
    menage: 'bg-emerald-500',
    stock: 'bg-orange-500',
    info: 'bg-red-500',
    production: 'bg-purple-500',
    equipe: 'bg-indigo-500',
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Configuration du rapport de transmission</span>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-transmission-config">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enregistrer
            </Button>
          </CardTitle>
          <p className="text-sm text-muted-foreground">Activez/désactivez les sections et champs du formulaire de transmission de service</p>
        </CardHeader>
      </Card>

      {/* Checklist */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            Checklist rapide
            <Button variant="outline" size="sm" className="gap-1" onClick={addChecklist}><Plus className="w-3.5 h-3.5" /> Ajouter</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {config.checklist.map((c, cIdx) => (
              <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg border bg-muted/20">
                <Checkbox checked={c.enabled} onCheckedChange={() => toggleChecklist(cIdx)} data-testid={`config-check-${c.id}`} />
                <Input
                  value={c.label}
                  onChange={e => updateChecklistLabel(cIdx, e.target.value)}
                  className="h-8 text-sm flex-1"
                  data-testid={`config-check-label-${c.id}`}
                />
                {c.enabled ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeChecklist(cIdx)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      {config.sections.map((section, sIdx) => (
        <Card key={section.id} className={!section.enabled ? 'opacity-60' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-3">
              <Checkbox checked={section.enabled} onCheckedChange={() => toggleSection(sIdx)} data-testid={`config-section-${section.id}`} />
              <div className={`w-6 h-6 rounded flex items-center justify-center ${sectionColors[section.id] || 'bg-gray-500'}`}>
                <span className="text-white text-xs font-bold">{section.label[0]}</span>
              </div>
              <Input
                value={section.label}
                onChange={e => updateSectionLabel(sIdx, e.target.value)}
                className="h-8 text-sm font-semibold max-w-[250px]"
                data-testid={`config-section-label-${section.id}`}
              />
              <div className="ml-auto flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{section.fields.filter(f => f.enabled).length}/{section.fields.length} champs</Badge>
                <Button variant="outline" size="sm" className="gap-1 h-7" onClick={() => addField(sIdx)}><Plus className="w-3 h-3" /> Champ</Button>
              </div>
            </CardTitle>
          </CardHeader>
          {section.enabled && (
            <CardContent>
              <div className="space-y-1.5">
                {section.fields.map((field, fIdx) => (
                  <div key={field.id} className={`flex items-center gap-3 p-2 rounded border ${field.enabled ? 'bg-background' : 'bg-muted/30'}`}>
                    <GripVertical className="w-4 h-4 text-muted-foreground/40" />
                    <Checkbox checked={field.enabled} onCheckedChange={() => toggleField(sIdx, fIdx)} data-testid={`config-field-${field.id}`} />
                    <Input
                      value={field.label}
                      onChange={e => updateFieldLabel(sIdx, fIdx, e.target.value)}
                      className="h-7 text-sm flex-1"
                      data-testid={`config-field-label-${field.id}`}
                    />
                    <Badge variant="secondary" className="text-[10px]">{field.type}</Badge>
                    {field.enabled ? <Eye className="w-3.5 h-3.5 text-green-600" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeField(sIdx, fIdx)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

